import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWarehouse } from '../../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { crossDockService, CrossDockSuggestion } from '../../../../../services/crossDockService';
import { stcBinService } from '../../../../../services/stcBinService';

/**
 * Crossdock-forslag i varemottak ved restordre (TW 36395306).
 * Plan: Claude md files/Crossdock-Mottak-Restordre/Plan-Del-A.md, punkt 3.0.
 *
 * All crossdock-logikk for mottaksskjermen ligger her, så POrderReceivingDetail bare får
 * noen få koblingspunkter:
 * - Crossdock av for lageret (Warehouse.GRCrossDockingYesNo) → ingen kall, alle sjekker er no-op.
 * - Forslaget hentes i bakgrunnen når linjen aktiveres. Feltene låses aldri.
 * - `ensureReadyForSave` venter på svaret (maks SUGGESTION_TIMEOUT_MS) før lagring, så operatøren
 *   ikke kan lagre uten å ha sett spørsmålet. Svarer ikke API-et, lagres linjen som vanlig ankomst.
 * - Svaret (Ja/Nei) huskes bare mens linjen er aktiv. Ny aktivering = nytt spørsmål.
 * - Crossdock-lokasjonene hentes én gang per lager, ikke per linje.
 */

const SUGGESTION_TIMEOUT_MS = 2000;

export type CrossDockDecision = 'yes' | 'no' | null;

export interface UseCrossDockSuggestionOptions {
  /** Lageret linjen mottas på */
  warehouseId?: string;
  /** Firma for lokasjonsoppslaget */
  companyNo?: string;
  /** Varen på den aktive linjen (tom = ingen aktiv linje) */
  itemNo?: string;
  /** Endres når en annen linje aktiveres (ny linje eller en annen ankomstlinje) */
  lineKey?: string;
  /** Antallet operatøren har tastet på linjen */
  quantity: number;
  /** Slår av crossdock uansett lageroppsett (bilmottak/samlemottak er utenfor v1) */
  disabled?: boolean;
}

export interface UseCrossDockSuggestionResult {
  /** Vis panelet (spørsmål på Pro-lager, melding på Standard-lager) */
  visible: boolean;
  suggestion: CrossDockSuggestion | null;
  decision: CrossDockDecision;
  setDecision: (decision: CrossDockDecision) => void;
  crossDockBins: string[];
  binCode: string;
  setBinCode: (binCode: string) => void;
  crossDockQty: string;
  setCrossDockQty: (qty: string) => void;
  /** Melding til operatøren (manglende svar, tidsavbrudd, feil) */
  message: string;
  /** Før «Lagre»: false = ikke lagre ennå (spørsmålet må besvares, eller ugyldig crossdock-valg) */
  ensureReadyForSave: () => Promise<boolean>;
  /** Før «Direktemottak»: false når crossdock er valgt eller spørsmålet ikke er besvart */
  ensureReadyForDirectReceipt: () => Promise<boolean>;
  /** Etter at ankomstlinjen er lagret: legger crossdock-delen ut når operatøren svarte Ja. Returnerer feiltekst eller null. */
  completeAfterSave: (batchId: string, lineId: number | undefined) => Promise<string | null>;
  /** Operatøren har valgt Ja: lagreknappen lagrer bare crossdock-delen */
  isCrossDockMode: boolean;
  /** Antallet som legges på crossdock */
  crossDockQuantity: number;
  /** Teksten på lagreknappen i crossdock-modus, f.eks. «Legg 1 på CD002» */
  saveLabel?: string;
  /** Henter forslaget på nytt for samme linje (etter crossdock-utlegg, når linjen står åpen med rest) */
  refresh: () => void;
}

export function useCrossDockSuggestion({
  warehouseId,
  companyNo,
  itemNo,
  lineKey,
  quantity,
  disabled = false
}: UseCrossDockSuggestionOptions): UseCrossDockSuggestionResult {
  const { t } = useTranslation();
  const { warehouses } = useWarehouse();

  const warehouse = useMemo(
    () => warehouses.find(w => w.warehouseID === warehouseId),
    [warehouses, warehouseId]
  );
  const enabled = !disabled && warehouse?.grCrossDockingYesNo === true;

  const [suggestion, setSuggestion] = useState<CrossDockSuggestion | null>(null);
  const [decision, setDecisionState] = useState<CrossDockDecision>(null);
  const [binCode, setBinCode] = useState('');
  const [crossDockQty, setCrossDockQtyState] = useState('');
  const [qtyOverridden, setQtyOverridden] = useState(false);
  const [message, setMessage] = useState('');
  const [crossDockBins, setCrossDockBins] = useState<string[]>([]);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const pendingRef = useRef<Promise<CrossDockSuggestion | null> | null>(null);
  const suggestionRef = useRef<CrossDockSuggestion | null>(null);
  const binsCacheRef = useRef<Map<string, string[]>>(new Map());

  // Crossdock-lokasjonene: én gang per lager (Pro-lager med crossdock på)
  useEffect(() => {
    if (!enabled || !warehouse?.useAWYesNo || !warehouseId || !companyNo) {
      setCrossDockBins([]);
      return;
    }
    const cached = binsCacheRef.current.get(warehouseId);
    if (cached) {
      setCrossDockBins(cached);
      return;
    }
    let cancelled = false;
    stcBinService.getCrossDockLocations(companyNo, warehouseId)
      .then(bins => {
        const codes = bins.map(b => b.binCode);
        binsCacheRef.current.set(warehouseId, codes);
        if (!cancelled) setCrossDockBins(codes);
      })
      .catch(err => console.error('Crossdock: kunne ikke hente crossdock-lokasjoner', err));
    return () => { cancelled = true; };
  }, [enabled, warehouse?.useAWYesNo, warehouseId, companyNo]);

  // Forslaget: på nytt hver gang en linje aktiveres. Ingen kall når crossdock er av.
  useEffect(() => {
    setSuggestion(null);
    suggestionRef.current = null;
    setDecisionState(null);
    setBinCode('');
    setCrossDockQtyState('');
    setQtyOverridden(false);
    setMessage('');
    pendingRef.current = null;

    if (!enabled || !itemNo || !warehouseId) return;

    const controller = new AbortController();
    pendingRef.current = crossDockService
      .getSuggestion(warehouseId, itemNo, quantity, controller.signal)
      .then(result => {
        if (controller.signal.aborted) return null;
        suggestionRef.current = result;
        setSuggestion(result);
        return result;
      })
      .catch(err => {
        if (!controller.signal.aborted) console.error('Crossdock: kunne ikke hente forslag', err);
        return null;
      });

    return () => controller.abort();
    // Antallet endrer ikke forslaget (beregnes lokalt under), så det er ikke med her.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, itemNo, warehouseId, lineKey, refreshNonce]);

  // Forvalgt lokasjon: lagerets forvalg hvis det er en crossdock-lokasjon, ellers eneste lokasjon
  useEffect(() => {
    if (!suggestion?.hasSuggestion || binCode) return;
    const preferred = suggestion.defaultCrossDockBinCode?.trim();
    const match = preferred ? crossDockBins.find(b => b.toLowerCase() === preferred.toLowerCase()) : undefined;
    if (match) setBinCode(match);
    else if (crossDockBins.length === 1) setBinCode(crossDockBins[0]);
  }, [suggestion, crossDockBins, binCode]);

  // Foreslått antall følger linjens antall til operatøren endrer det selv
  useEffect(() => {
    if (!suggestion?.hasSuggestion || qtyOverridden) return;
    const suggested = quantity > 0 ? Math.min(suggestion.demandQty, quantity) : suggestion.demandQty;
    setCrossDockQtyState(String(Math.floor(suggested)));
  }, [suggestion, quantity, qtyOverridden]);

  const setCrossDockQty = useCallback((qty: string) => {
    setQtyOverridden(true);
    setCrossDockQtyState(qty);
  }, []);

  const setDecision = useCallback((value: CrossDockDecision) => {
    setDecisionState(value);
    setMessage('');
  }, []);

  /** Venter på forslaget, men aldri lenger enn tidsgrensen. null = ikke noe forslag/tidsavbrudd. */
  const waitForSuggestion = useCallback(async (): Promise<{ result: CrossDockSuggestion | null; timedOut: boolean }> => {
    if (!enabled || !pendingRef.current) return { result: suggestionRef.current, timedOut: false };
    if (suggestionRef.current) return { result: suggestionRef.current, timedOut: false };

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>(resolve => { timer = setTimeout(() => resolve('timeout'), SUGGESTION_TIMEOUT_MS); });
    const outcome = await Promise.race([pendingRef.current, timeout]);
    if (timer) clearTimeout(timer);
    return outcome === 'timeout' ? { result: null, timedOut: true } : { result: outcome, timedOut: false };
  }, [enabled]);

  const needsAnswer = (result: CrossDockSuggestion | null) =>
    !!result?.hasSuggestion && result.isAdvancedWarehouse;

  const ensureReadyForSave = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true;
    const { result, timedOut } = await waitForSuggestion();
    if (timedOut) {
      setMessage(t('warehouse.modules.receipt.crossDock.timeout', {}, 'Crossdock-sjekken svarte ikke. Linjen lagres som vanlig ankomst.'));
      return true;
    }
    if (!needsAnswer(result)) return true;

    if (decision === null) {
      setMessage(t('warehouse.modules.receipt.crossDock.answerFirst', {}, 'Svar på crossdock-spørsmålet før du lagrer.'));
      return false;
    }
    if (decision === 'yes') {
      const qty = parseInt(crossDockQty, 10) || 0;
      if (!binCode) {
        setMessage(t('warehouse.modules.receipt.crossDock.chooseBin', {}, 'Velg crossdock-lokasjon.'));
        return false;
      }
      if (qty <= 0 || qty > quantity) {
        setMessage(t('warehouse.modules.receipt.crossDock.invalidQty', { '0': String(quantity) }, `Antall til crossdock må være mellom 1 og ${quantity}.`));
        return false;
      }
    }
    return true;
  }, [enabled, waitForSuggestion, decision, crossDockQty, binCode, quantity, t]);

  const ensureReadyForDirectReceipt = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true;
    const { result } = await waitForSuggestion();
    if (!needsAnswer(result) || decision === 'no') return true;

    setMessage(decision === 'yes'
      ? t('warehouse.modules.receipt.crossDock.useSave', {}, 'Crossdock er valgt. Trykk Lagre for å legge varen på crossdock.')
      : t('warehouse.modules.receipt.crossDock.answerFirst', {}, 'Svar på crossdock-spørsmålet før du lagrer.'));
    return false;
  }, [enabled, waitForSuggestion, decision, t]);

  const completeAfterSave = useCallback(async (batchId: string, lineId: number | undefined): Promise<string | null> => {
    if (!enabled || decision !== 'yes' || !needsAnswer(suggestionRef.current)) return null;
    if (!lineId) {
      return t('warehouse.modules.receipt.crossDock.putAwayFailed', { '0': '' }, 'Linjen ble lagret som ankomst, men crossdock-utlegget kunne ikke gjøres.');
    }
    try {
      await crossDockService.putAway(batchId, lineId, binCode, parseInt(crossDockQty, 10) || 0);
      return null;
    } catch (err) {
      const detail = err instanceof Error ? err.message : '';
      return t('warehouse.modules.receipt.crossDock.putAwayFailed', { '0': detail },
        `Linjen ble lagret som ankomst, men crossdock-utlegget feilet: ${detail}`);
    } finally {
      setDecisionState(null);
    }
  }, [enabled, decision, binCode, crossDockQty, t]);

  const isCrossDockMode = enabled && decision === 'yes' && needsAnswer(suggestion);
  const crossDockQuantity = parseInt(crossDockQty, 10) || 0;
  const refresh = useCallback(() => setRefreshNonce(n => n + 1), []);

  return {
    isCrossDockMode,
    crossDockQuantity,
    saveLabel: isCrossDockMode
      ? t('warehouse.modules.receipt.crossDock.saveButton', { '0': String(crossDockQuantity), '1': binCode || '…' },
          `Legg ${crossDockQuantity} på ${binCode || '…'}`)
      : undefined,
    refresh,
    visible: enabled && !!suggestion?.hasSuggestion,
    suggestion,
    decision,
    setDecision,
    crossDockBins,
    binCode,
    setBinCode,
    crossDockQty,
    setCrossDockQty,
    message,
    ensureReadyForSave,
    ensureReadyForDirectReceipt,
    completeAfterSave
  };
}
